import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Fingerprint as TimbraIcon, Clock3, CalendarClock, ClipboardList, CircleDot, ChevronLeft, ChevronRight, Edit as EditIcon, Delete as DeleteIcon } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';

const statoColor = {
  NON_TIMBRATO: 'default',
  IN_SERVIZIO: 'success',
  IN_PAUSA: 'warning',
  USCITO: 'default'
};

const normalizeTipoTimbratura = (value) => String(value || '').trim().toLowerCase();

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const StatWidgetHr = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{ p: 2, display: 'flex', alignItems: 'center', height: '100%' }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function HrTimbra() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timbrature, setTimbrature] = useState([]);
  const [presenzaOggi, setPresenzaOggi] = useState(null);
  const [apiError, setApiError] = useState('');
  const [dipendentiOptions, setDipendentiOptions] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [realignSaving, setRealignSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    dipendente_id: '',
    data: '',
    ora: '',
    tipo_timbratura: 'entrata',
    note: ''
  });
  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctSaving, setCorrectSaving] = useState(false);
  const [correctForm, setCorrectForm] = useState({
    id: null,
    data: '',
    ora: '',
    tipo_timbratura: 'entrata',
    note: ''
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const getLocalDateIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const today = useMemo(() => getLocalDateIso(), []);
  const initialSelectedDate = useMemo(() => {
    const q = String(searchParams.get('date') || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(q)) return q;
    return today;
  }, [searchParams, today]);
  const initialSelectedDipendenteId = useMemo(() => {
    const q = String(searchParams.get('dipendente_id') || '').trim();
    return /^\d+$/.test(q) ? q : '';
  }, [searchParams]);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [selectedDipendenteId, setSelectedDipendenteId] = useState(initialSelectedDipendenteId);
  const giornoLabel = useMemo(() => {
    const d = new Date(`${selectedDate}T00:00:00`);
    return d.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }, [selectedDate]);
  const isManageUser = useMemo(() => {
    const role = String(user?.role || '').toLowerCase();
    const dep = String(user?.department_name || '').toLowerCase();
    return role === 'admin' || dep.includes('hr') || dep.includes('risorse umane') || dep.includes('personale');
  }, [user?.department_name, user?.role]);
  const isLegalDepartment = useMemo(() => {
    const dep = String(user?.department_name || '').toLowerCase();
    return dep.includes('legale') || dep.includes('legal');
  }, [user?.department_name]);

  const labels = useMemo(() => {
    if (!isLegalDepartment) {
      return {
        pageTitle: 'Timbra',
        pageSubtitle: 'Timbratura intelligente e stato giornaliero dipendente.',
        widgetState: 'Stato giornata',
        widgetWorked: 'Ore lavorate',
        widgetDelay: 'Ritardo',
        mainStart: 'TIMBRA ENTRATA',
        mainEnd: 'TIMBRA USCITA',
        mainResume: 'TIMBRA FINE PAUSA',
        pause: 'TIMBRA INIZIO PAUSA',
        historyTitle: 'Cronologia timbrature',
        historicAlert: (d) => `Stai consultando una giornata storica (${d}): la timbratura e disabilitata in sola lettura.`,
        blockedAlert:
          "Nessun profilo dipendente associato all'utente autenticato. Timbratura bloccata: contatta HR per associare il profilo.",
        tipoLabel: (tipo) => tipo || '-',
        savedToast: (tipo) => `Timbratura ${tipo} registrata`,
        apiErrorFallback: 'Dati timbrature non disponibili (errore API)',
        genericError: 'Errore durante la timbratura',
        realignError: 'Errore riallineamento timbrature',
        realignCompleted: 'Riallineamento completato',
        validateFieldsError: 'Compila data, ora e tipo timbratura',
        manualSaved: 'Timbratura manuale registrata',
        manualError: 'Errore inserimento manuale',
        correctSaved: 'Timbratura corretta',
        correctError: 'Errore correzione timbratura',
        deleteSaved: 'Timbratura eliminata',
        deleteError: 'Errore eliminazione timbratura',
        lastEventLabel: 'Ultima timbratura',
        savingLabel: 'Timbratura...',
        emptyHistory: 'Nessuna timbratura per la data selezionata',
        tooltipEdit: 'Correggi timbratura',
        tooltipDelete: 'Elimina timbratura',
        dialogManualTitle: 'Inserimento manuale timbratura',
        dialogCorrectTitle: 'Correggi timbratura',
        dialogDeleteText: (details) => `Confermi l'eliminazione della timbratura${details ? ` ${details}` : ''}?`
      };
    }

    return {
      pageTitle: 'Pratiche',
      pageSubtitle: 'Inizio/pausa/fine gestione pratiche e stato giornaliero.',
      widgetState: 'Stato gestione',
      widgetWorked: 'Tempo gestione',
      widgetDelay: 'Ritardo',
      mainStart: 'PRATICHE (inizio gestione)',
      mainEnd: 'PRATICHE (fine gestione)',
      mainResume: 'PRATICHE (riprendi gestione)',
      pause: 'PRATICHE (pausa gestione)',
      historyTitle: 'Cronologia gestione pratiche',
      historicAlert: (d) => `Stai consultando una giornata storica (${d}): la gestione e disabilitata in sola lettura.`,
      blockedAlert:
        "Profilo non configurato per la gestione pratiche. Contatta l'amministratore per associare il profilo.",
      tipoLabel: (tipo) => {
        const t = normalizeTipoTimbratura(tipo);
        if (t === 'entrata') return 'inizio gestione';
        if (t === 'inizio_pausa') return 'pausa gestione';
        if (t === 'fine_pausa') return 'riprendi gestione';
        if (t === 'uscita') return 'fine gestione';
        return tipo || '-';
      },
      savedToast: () => 'Evento gestione registrato',
      apiErrorFallback: 'Dati gestione non disponibili (errore API)',
      genericError: 'Errore durante la gestione',
      realignError: 'Errore riallineamento gestione',
      realignCompleted: 'Riallineamento completato',
      validateFieldsError: 'Compila data, ora e tipo',
      manualSaved: 'Evento registrato',
      manualError: 'Errore inserimento evento',
      correctSaved: 'Evento aggiornato',
      correctError: 'Errore aggiornamento evento',
      deleteSaved: 'Evento eliminato',
      deleteError: 'Errore eliminazione evento',
      lastEventLabel: 'Ultimo evento',
      savingLabel: 'Salvataggio...',
      emptyHistory: 'Nessun evento per la data selezionata',
      tooltipEdit: 'Correggi evento',
      tooltipDelete: 'Elimina evento',
      dialogManualTitle: 'Inserimento manuale',
      dialogCorrectTitle: 'Correggi evento',
      dialogDeleteText: (details) => `Confermi l'eliminazione dell'evento${details ? ` ${details}` : ''}?`
    };
  }, [isLegalDepartment]);

  const mapStatoFromTimbrature = (rows) => {
    if (!rows.length) return 'NON_TIMBRATO';
    const last = rows[0];
    const tipo = normalizeTipoTimbratura(last.tipo_timbratura);
    if (tipo === 'entrata' || tipo === 'fine_pausa') return 'IN_SERVIZIO';
    if (tipo === 'inizio_pausa') return 'IN_PAUSA';
    if (tipo === 'uscita') return 'USCITO';
    return 'NON_TIMBRATO';
  };

  const deriveFlowState = (rows) => {
    const sorted = [...rows].sort((a, b) => new Date(a.data_ora_timbratura) - new Date(b.data_ora_timbratura));
    let inWork = false;
    let inPause = false;
    sorted.forEach((row) => {
      const tipo = normalizeTipoTimbratura(row.tipo_timbratura);
      if (tipo === 'entrata') {
        // Supporta dataset legacy: una nuova entrata durante una pausa chiude la pausa.
        inWork = true;
        inPause = false;
      } else if (tipo === 'inizio_pausa' && inWork) {
        inPause = true;
      } else if (tipo === 'fine_pausa' && inWork) {
        inPause = false;
      } else if (tipo === 'uscita' && inWork) {
        inWork = false;
        inPause = false;
      }
    });
    return { inWork, inPause };
  };

  const toHm = (value) => {
    if (!value) return '00:00';
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      const totalMinutes = Math.max(0, Math.round(numeric * 60));
      const hh = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
      const mm = String(totalMinutes % 60).padStart(2, '0');
      return `${hh}:${mm}`;
    }
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(str)) {
      const timePart = str.includes('T') ? str.split('T')[1] : str.split(' ')[1];
      return String(timePart || '').slice(0, 5);
    }
    if (str.includes('T')) {
      const d = new Date(str);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }
    return str.slice(0, 5);
  };

  const toDateIt = (value) => {
    if (!value) return '-';
    const str = String(value);
    const datePart = str.includes('T') ? str.split('T')[0] : str.split(' ')[0];
    const p = datePart.split('-');
    if (p.length === 3) return `${p[2]}/${p[1]}/${p[0]}`;
    return datePart;
  };

  const getHourFromDateTime = (value) => {
    if (!value) return null;
    const str = String(value);
    const match = str.match(/[ T](\d{2}):(\d{2})/);
    if (match) {
      const hh = Number(match[1]);
      if (!Number.isNaN(hh)) return hh;
    }
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.getHours();
  };

  const toMinutesFromTime = (value) => {
    if (!value) return null;
    const str = String(value);
    const match = str.match(/(\d{2}):(\d{2})/);
    if (!match) return null;
    const hh = Number(match[1]);
    const mm = Number(match[2]);
    if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
    return (hh * 60) + mm;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setApiError('');
    try {
      const params = { data_lavoro: selectedDate, limit: 200 };
      const presenzaParams = { data_da: selectedDate, data_a: selectedDate };
      if (isManageUser && selectedDipendenteId) {
        params.dipendente_id = selectedDipendenteId;
        presenzaParams.dipendente_id = selectedDipendenteId;
      }
      const [timRes, presRes] = await Promise.all([
        api.get('/timbrature', { params }),
        api.get('/presenze-giornaliere', { params: presenzaParams })
      ]);
      setTimbrature(timRes.data || []);
      setPresenzaOggi((presRes.data || [])[0] || null);
    } catch (error) {
      console.error('Errore caricamento timbrature/presenze:', error);
      setTimbrature([]);
      setPresenzaOggi(null);
      setApiError(error.response?.data?.message || labels.apiErrorFallback);
    } finally {
      setLoading(false);
    }
  }, [isManageUser, labels.apiErrorFallback, selectedDate, selectedDipendenteId]);

  const loadDipendenti = useCallback(async () => {
    if (!isManageUser) return;
    try {
      const res = await api.get('/dipendenti', { params: { limit: 300, offset: 0, sortBy: 'cognome', sortDir: 'ASC' } });
      const options = (res.data?.items || []).map((d) => ({
        id: String(d.id),
        label: `${d.cognome || ''} ${d.nome || ''}`.trim()
      }));
      setDipendentiOptions(options);
    } catch (error) {
      console.error('Errore caricamento dipendenti:', error);
      setDipendentiOptions([]);
    }
  }, [isManageUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedDate(initialSelectedDate);
  }, [initialSelectedDate]);

  useEffect(() => {
    setSelectedDipendenteId(initialSelectedDipendenteId);
  }, [initialSelectedDipendenteId]);

  useEffect(() => {
    loadDipendenti();
  }, [loadDipendenti]);

  const handleQuickTipo = async (tipoTimbratura) => {
    if (noProfileBlocked) {
      toast.error(isLegalDepartment ? 'Operazione bloccata: profilo non associato' : 'Timbratura bloccata: nessun profilo dipendente associato');
      return;
    }
    try {
      setSaving(true);
      const response = await api.post('/timbrature', { tipo_timbratura: tipoTimbratura });
      if (response.data?.warningMessage) {
        toast.warning(response.data.warningMessage, { autoClose: 6000 });
      } else {
        toast.success(labels.savedToast(tipoTimbratura));
      }
      await loadData();
    } catch (error) {
      console.error('Errore timbratura rapida:', error);
      toast.error(error.response?.data?.message || labels.genericError);
    } finally {
      setSaving(false);
    }
  };

  const handleRealignToday = async () => {
    if (!isManageUser) return;
    try {
      setRealignSaving(true);
      const res = await api.post('/timbrature/riallinea-oggi', {});
      toast.success(res.data?.message || labels.realignCompleted);
      await loadData();
    } catch (error) {
      console.error('Errore riallineamento timbrature:', error);
      toast.error(error.response?.data?.message || labels.realignError);
    } finally {
      setRealignSaving(false);
    }
  };

  const openManualDialog = () => {
    if (!isManageUser) return;
    const now = new Date();
    const data = now.toISOString().slice(0, 10);
    const ora = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    setManualForm({
      dipendente_id: dipendentiOptions[0]?.id || '',
      data,
      ora,
      tipo_timbratura: 'entrata',
      note: ''
    });
    setManualOpen(true);
  };

  const handleManualSave = async () => {
    if (!manualForm.data || !manualForm.ora || !manualForm.tipo_timbratura) {
      toast.error(labels.validateFieldsError);
      return;
    }
    if (!manualForm.note.trim()) {
      toast.error('Inserisci una motivazione');
      return;
    }
    if (isManageUser && !manualForm.dipendente_id) {
      toast.error('Seleziona un dipendente');
      return;
    }
    try {
      setManualSaving(true);
      await api.post('/timbrature', {
        dipendente_id: manualForm.dipendente_id ? Number(manualForm.dipendente_id) : undefined,
        data_ora_timbratura: `${manualForm.data}T${manualForm.ora}:00`,
        tipo_timbratura: manualForm.tipo_timbratura,
        origine_inserimento: 'manuale',
        note: manualForm.note.trim()
      });
      toast.success(labels.manualSaved);
      setManualOpen(false);
      await loadData();
    } catch (error) {
      console.error('Errore inserimento manuale:', error);
      toast.error(error.response?.data?.message || labels.manualError);
    } finally {
      setManualSaving(false);
    }
  };

  const openCorrectDialog = (row) => {
    if (!isManageUser || !row?.id) return;
    const dt = new Date(row.data_ora_timbratura);
    const validDate = !Number.isNaN(dt.getTime());
    setCorrectForm({
      id: row.id,
      data: validDate ? dt.toISOString().slice(0, 10) : today,
      ora: validDate ? `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}` : '09:00',
      tipo_timbratura: row.tipo_timbratura || 'entrata',
      note: row.note || ''
    });
    setCorrectOpen(true);
  };

  const handleCorrectSave = async () => {
    if (!correctForm.id) return;
    if (!correctForm.data || !correctForm.ora || !correctForm.tipo_timbratura) {
      toast.error(labels.validateFieldsError);
      return;
    }
    if (!String(correctForm.note || '').trim()) {
      toast.error('Inserisci una motivazione correzione');
      return;
    }
    try {
      setCorrectSaving(true);
      await api.put(`/timbrature/${correctForm.id}`, {
        data_ora_timbratura: `${correctForm.data}T${correctForm.ora}:00`,
        tipo_timbratura: correctForm.tipo_timbratura,
        note: String(correctForm.note || '').trim()
      });
      toast.success(labels.correctSaved);
      setCorrectOpen(false);
      await loadData();
    } catch (error) {
      console.error('Errore correzione timbratura:', error);
      toast.error(error.response?.data?.message || labels.correctError);
    } finally {
      setCorrectSaving(false);
    }
  };

  const openDeleteDialog = (row) => {
    if (!isManageUser || !row?.id) return;
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteSaving(true);
      await api.delete(`/timbrature/${deleteTarget.id}`);
      toast.success(labels.deleteSaved);
      setDeleteOpen(false);
      setDeleteTarget(null);
      await loadData();
    } catch (error) {
      console.error('Errore eliminazione timbratura:', error);
      toast.error(error.response?.data?.message || labels.deleteError);
    } finally {
      setDeleteSaving(false);
    }
  };

  const statoGiornata = mapStatoFromTimbrature(timbrature);
  const flowState = deriveFlowState(timbrature);
  const isTodaySelected = selectedDate === today;
  const noProfileBlocked = String(apiError || '').toLowerCase().includes('nessun profilo dipendente');
  const statoGiornataDisplay = useMemo(() => {
    if (!isLegalDepartment) return statoGiornata;
    if (statoGiornata === 'NON_TIMBRATO') return 'NON_IN_GESTIONE';
    if (statoGiornata === 'IN_SERVIZIO') return 'IN_GESTIONE';
    if (statoGiornata === 'IN_PAUSA') return 'PAUSA_GESTIONE';
    if (statoGiornata === 'USCITO') return 'FINE_GESTIONE';
    return statoGiornata;
  }, [isLegalDepartment, statoGiornata]);
  const suspectedTimezoneAnomaly = useMemo(
    () => timbrature.some((row) => {
      const hh = getHourFromDateTime(row.data_ora_timbratura);
      return hh !== null && hh < 5;
    }),
    [timbrature]
  );
  const mismatchPresenzeVsTimbrature = useMemo(() => {
    const firstEntrata = timbrature
      .filter((row) => row.tipo_timbratura === 'entrata')
      .map((row) => toHm(row.data_ora_timbratura))
      .map((hhmm) => toMinutesFromTime(hhmm))
      .filter((v) => v !== null)
      .sort((a, b) => a - b)[0];
    const presenzaPrimaEntrata = toMinutesFromTime(presenzaOggi?.prima_entrata || null);
    if (firstEntrata === undefined || firstEntrata === null || presenzaPrimaEntrata === null) return false;
    return Math.abs(firstEntrata - presenzaPrimaEntrata) >= 30;
  }, [presenzaOggi?.prima_entrata, timbrature]);
  const canShowRealign = isManageUser && isTodaySelected && (suspectedTimezoneAnomaly || mismatchPresenzeVsTimbrature);
  const ultimaTimbratura = timbrature[0]
    ? `${labels.tipoLabel(timbrature[0].tipo_timbratura)} - ${toHm(timbrature[0].data_ora_timbratura)}`
    : '-';
  const nextTimbraturaLabel = flowState.inPause
    ? labels.mainResume
    : (flowState.inWork ? labels.mainEnd : labels.mainStart);
  const oreLavorate = presenzaOggi?.ore_lavorate !== undefined ? toHm(presenzaOggi.ore_lavorate) : '00:00';
  const ritardo = presenzaOggi?.minuti_ritardo !== undefined ? toHm(Number(presenzaOggi.minuti_ritardo || 0) / 60) : '00:00';
  const dipendenteLabel = useMemo(() => {
    if (isManageUser && selectedDipendenteId) {
      const found = dipendentiOptions.find((d) => String(d.id) === String(selectedDipendenteId));
      if (found?.label) return found.label;
    }
    return user?.name || 'Dipendente';
  }, [dipendentiOptions, isManageUser, selectedDipendenteId, user?.name]);
  const goPrevDay = () => {
    const d = new Date(`${selectedDate}T00:00:00`);
    d.setDate(d.getDate() - 1);
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };
  const goNextDay = () => {
    if (selectedDate >= today) return;
    const d = new Date(`${selectedDate}T00:00:00`);
    d.setDate(d.getDate() + 1);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setSelectedDate(iso > today ? today : iso);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <TimbraIcon size={20} color="#4f46e5" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {labels.pageTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {labels.pageSubtitle}
          </Typography>
        </Box>
      </Box>

      {noProfileBlocked && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {labels.blockedAlert}
        </Alert>
      )}
      {!isTodaySelected && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {labels.historicAlert(toDateIt(selectedDate))}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <StatWidgetHr title={labels.widgetState} value={statoGiornataDisplay} icon={CalendarClock} color="#6366f1" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatWidgetHr title={labels.widgetWorked} value={oreLavorate} icon={Clock3} color="#22c55e" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatWidgetHr title={labels.widgetDelay} value={ritardo} icon={CircleDot} color="#f59e0b" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <StyledCard sx={{ p: 2, height: '100%', minHeight: 190 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {labels.widgetState}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Button size="small" variant="outlined" onClick={goPrevDay} sx={{ minWidth: 34, px: 0.25 }}>
                  <ChevronLeft size={16} />
                </Button>
                <Typography variant="body2" color="text.secondary">
                  Giornata: <strong>{giornoLabel.charAt(0).toUpperCase() + giornoLabel.slice(1)}</strong>
                </Typography>
                <Button size="small" variant="outlined" onClick={goNextDay} disabled={selectedDate >= today} sx={{ minWidth: 34, px: 0.25 }}>
                  <ChevronRight size={16} />
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {dipendenteLabel}
              </Typography>
              <Chip
                label={statoGiornataDisplay}
                size="small"
                color={statoColor[statoGiornata] || 'default'}
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              {labels.lastEventLabel}: {ultimaTimbratura}
            </Typography>
            {apiError && (
              <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                {apiError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => handleQuickTipo(flowState.inPause ? 'fine_pausa' : (flowState.inWork ? 'uscita' : 'entrata'))}
                disabled={saving || loading || noProfileBlocked || !isTodaySelected}
              >
                {saving ? labels.savingLabel : nextTimbraturaLabel}
              </Button>
              {flowState.inWork && !flowState.inPause && (
                <Button
                  variant="outlined"
                  onClick={() => handleQuickTipo('inizio_pausa')}
                  disabled={saving || loading || noProfileBlocked || !isTodaySelected}
                >
                  {labels.pause}
                </Button>
              )}
              {isManageUser && (
                <Button variant="outlined" onClick={openManualDialog} disabled={noProfileBlocked}>
                  Inserimento manuale
                </Button>
              )}
              {canShowRealign && (
                <Button variant="outlined" color="warning" onClick={handleRealignToday} disabled={realignSaving}>
                  {realignSaving ? 'Riallineamento...' : 'Riallinea giornata odierna'}
                </Button>
              )}
            </Box>
            {isTodaySelected && !flowState.inWork && !flowState.inPause && timbrature.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Nuova entrata nella stessa giornata.
              </Typography>
            )}
          </StyledCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledCard sx={{ p: 2, height: '100%', minHeight: 190 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Riepilogo
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              {labels.widgetWorked}: <strong>{oreLavorate}</strong>
            </Typography>
            <Typography variant="body2">
              {labels.widgetDelay}: <strong>{ritardo}</strong>
            </Typography>
            <Typography variant="body2">
              Profilo: <strong>{user?.department_name || '-'}</strong>
            </Typography>
          </StyledCard>
        </Grid>
      </Grid>

      <StyledCard>
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid rgba(145, 158, 171, 0.24)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              bgcolor: 'rgba(99,102,241,0.10)',
              color: '#6366f1',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ClipboardList size={16} />
          </Box>
          <Typography variant="h6">{labels.historyTitle}</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'rgba(145, 158, 171, 0.08)' }}>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Orario</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Origine</TableCell>
                <TableCell>Note</TableCell>
                {isManageUser && <TableCell align="right">Azioni</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={isManageUser ? 6 : 5} align="center" sx={{ py: 2.5 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && timbrature.map((row) => (
                <TableRow key={row.id || `${row.data_ora_timbratura}-${row.tipo_timbratura}`}>
                  <TableCell>{toDateIt(row.data_ora_timbratura || row.data_lavoro)}</TableCell>
                  <TableCell>{toHm(row.data_ora_timbratura)}</TableCell>
                  <TableCell>{labels.tipoLabel(row.tipo_timbratura)}</TableCell>
                  <TableCell>{row.origine_inserimento || '-'}</TableCell>
                  <TableCell>{row.note || '-'}</TableCell>
                  {isManageUser && (
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', gap: 0.5 }}>
                        <Tooltip title={labels.tooltipEdit}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openCorrectDialog(row)}
                              disabled={noProfileBlocked}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={labels.tooltipDelete}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(row)}
                              disabled={noProfileBlocked}
                            >
                              <DeleteIcon size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!loading && timbrature.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isManageUser ? 6 : 5} align="center">
                    <Typography variant="body2" color="text.secondary">{labels.emptyHistory}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </StyledCard>

      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{labels.dialogManualTitle}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' } }}>
            <Autocomplete
              size="small"
              options={dipendentiOptions}
              getOptionLabel={(option) => option.label || ''}
              value={dipendentiOptions.find((d) => String(d.id) === String(manualForm.dipendente_id)) || null}
              onChange={(_, selected) => setManualForm((prev) => ({ ...prev, dipendente_id: selected ? String(selected.id) : '' }))}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              renderInput={(params) => <TextField {...params} label="Dipendente" />}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="manual-tipo-label">Tipo</InputLabel>
              <Select
                labelId="manual-tipo-label"
                label="Tipo"
                value={manualForm.tipo_timbratura}
                onChange={(e) => setManualForm((prev) => ({ ...prev, tipo_timbratura: e.target.value }))}
              >
                <MenuItem value="entrata">entrata</MenuItem>
                <MenuItem value="inizio_pausa">inizio_pausa</MenuItem>
                <MenuItem value="fine_pausa">fine_pausa</MenuItem>
                <MenuItem value="uscita">uscita</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data"
              type="date"
              size="small"
              value={manualForm.data}
              onChange={(e) => setManualForm((prev) => ({ ...prev, data: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Ora"
              type="time"
              size="small"
              value={manualForm.ora}
              onChange={(e) => setManualForm((prev) => ({ ...prev, ora: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Motivazione"
              size="small"
              value={manualForm.note}
              onChange={(e) => setManualForm((prev) => ({ ...prev, note: e.target.value }))}
              fullWidth
              sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)} disabled={manualSaving}>Annulla</Button>
          <Button onClick={handleManualSave} variant="contained" disabled={manualSaving}>
            {manualSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={correctOpen} onClose={() => setCorrectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{labels.dialogCorrectTitle}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' } }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="correct-tipo-label">Tipo</InputLabel>
              <Select
                labelId="correct-tipo-label"
                label="Tipo"
                value={correctForm.tipo_timbratura}
                onChange={(e) => setCorrectForm((prev) => ({ ...prev, tipo_timbratura: e.target.value }))}
              >
                <MenuItem value="entrata">entrata</MenuItem>
                <MenuItem value="inizio_pausa">inizio_pausa</MenuItem>
                <MenuItem value="fine_pausa">fine_pausa</MenuItem>
                <MenuItem value="uscita">uscita</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data"
              type="date"
              size="small"
              value={correctForm.data}
              onChange={(e) => setCorrectForm((prev) => ({ ...prev, data: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Ora"
              type="time"
              size="small"
              value={correctForm.ora}
              onChange={(e) => setCorrectForm((prev) => ({ ...prev, ora: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Motivazione correzione"
              size="small"
              value={correctForm.note}
              onChange={(e) => setCorrectForm((prev) => ({ ...prev, note: e.target.value }))}
              fullWidth
              sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorrectOpen(false)} disabled={correctSaving}>Annulla</Button>
          <Button onClick={handleCorrectSave} variant="contained" disabled={correctSaving}>
            {correctSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {labels.dialogDeleteText(
              deleteTarget
                ? `(${toDateIt(deleteTarget.data_ora_timbratura || deleteTarget.data_lavoro)} ${toHm(deleteTarget.data_ora_timbratura)} - ${labels.tipoLabel(deleteTarget.tipo_timbratura)})`
                : ''
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteSaving}>Annulla</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleteSaving}>
            {deleteSaving ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
